import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Pagination,
  Alert,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { apiService } from "../app/api";
import BaseLayout from "../layout/BaseLayout";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatTimestamp } from "../helper/formatTimestamp";
import { toast } from "react-toastify";
import { handleAxiosError } from "../helper/errorHandler";

interface LanguageItem {
  _id: string;
  language: string;
  is_active: boolean;
  createdAt?: string;
}

const Language: React.FC = () => {
  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");

  // Add Language Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newLanguages, setNewLanguages] = useState("");

  // Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const fetchLanguages = async (pageNumber: number, searchQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiService.get(
        `/languages?page=${pageNumber}&limit=16&search=${searchQuery}`
      );
      setLanguages(data.data);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch languages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchLanguages(page, search);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [page, search]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleAddLanguage = async () => {
    const list = newLanguages
      .split(",")
      .map((lang) => lang.trim())
      .filter((lang) => lang.length > 0);

    const uniqueList = Array.from(new Set(list));

    if (uniqueList.length === 0) {
      toast.warning("Please enter at least one language.");
      return;
    }

    const payload = uniqueList.map((lang) => ({ language: lang }));

    try {
      await apiService.post("/languages", payload);
      setAddDialogOpen(false);
      setNewLanguages("");
      fetchLanguages(page, search);
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  const handleDeleteLanguage = async () => {
    if (!deleteId) return;
    try {
      await apiService.delete(`/language/${deleteId}`);
      setDeleteDialogOpen(false);
      fetchLanguages(page, search);
    } catch (err: any) {
      handleAxiosError(err);
    }
  };

  return (
    <BaseLayout>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
      >
        <Typography fontSize={30} fontWeight={700}>
          Languages ({total})
        </Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="Search languages..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: "400px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setAddDialogOpen(true)}
          >
            Add Language
          </Button>
        </Box>
      </Box>

      {/* Loader */}
      {loading && (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {languages.length > 0 ? (
            <Grid container spacing={3} mt={3}>
              {languages.map((lang) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={lang._id}>
                  <Card
                    sx={{
                      boxShadow: 3,
                      borderRadius: 2,
                      "&:hover": { boxShadow: 6 },
                      height: "100%",
                      position: "relative",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" fontWeight={600}>
                        {lang.language}
                      </Typography>

                      {lang.createdAt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Added on: {formatTimestamp(lang.createdAt)}
                        </Typography>
                      )}

                      <IconButton
                        sx={{ position: "absolute", top: 8, right: 8 }}
                        onClick={() => {
                          setDeleteId(lang._id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography textAlign="center" mt={5}>
              No languages found.
            </Typography>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Add Language Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Languages</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={1}>
            Enter multiple languages without spaces, separated by commas.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={newLanguages}
            onChange={(e) =>
              setNewLanguages(e.target.value.replace(/\s+/g, "_").toLowerCase())
            }
            placeholder="english, english_india, hindi"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddLanguage}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Language</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this language?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteLanguage}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </BaseLayout>
  );
};

export default Language;
